import { useMemo } from "react";
import { map, reduce, reject } from "lodash";
import { getFacetName } from "@linkfluence/i18n";
import { Facet } from "@linkfluence/models/facet";
import { build } from "@linkfluence/models/facets-builder";
import { GeoLocation, GeoTag } from "@linkfluence/models/geolocations";
import { Locale } from "@linkfluence/models/locale";
import { Platform } from "@linkfluence/models/platform";
import { useCorpora } from "@radarly/core/corpora/use-cases";
import { useCustomFields } from "@radarly/core/custom-field/use-cases";
import { useGroups } from "@radarly/core/groups/use-cases";
import { useCurrentProjectPlatforms } from "@radarly/core/project/use-cases";
import { useCurrentProject } from "@radarly/core/project/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { Corpora } from "@radarly/models/corpora";
import { CustomField } from "@radarly/models/custom-field";
import { FormatedFacet, getFacetDescription } from "@radarly/models/facets";
import { Group } from "@radarly/models/groups";
import {
  useIsCurrentInsightPageGlobal,
  useIsInsightPage,
} from "@radarly/models/insight-pages/is";
import { Query } from "@radarly/models/queries";

type FormatedFacets = Record<string, FormatedFacet>;

const formatFacets = (
  facets: FormatedFacets,
  locale: Locale,
  isGlobalInsightPage: boolean,
  isInsightPage: boolean
): FormatedFacets => {
  return reduce(
    facets,
    (acc, facet, key: string) => {
      acc[key] = {
        ...facet,
        label: facet.label || getFacetName(locale, facet.id as Facet),
        description: getFacetDescription(
          locale,
          facet.id as Facet,
          isGlobalInsightPage,
          isInsightPage
        ),
        values: map(facet.values, (value) => {
          return {
            ...value,
            label:
              value.label ||
              // @ts-ignore
              value.name || // @TODO
              getFacetName(locale, facet.id as Facet, value.id),
          };
        }),
      };
      return acc;
    },
    {}
  );
};

const rejectEmptyValues = (values) =>
  reject(values, (value) => value.value === "");

function getImageRecognitionLogos(logos: number[]) {
  return map(logos, (logo) => ({
    id: logo,
    label: "logo_" + logo,
  }));
}
function getImageRecognitionPersonIdentifications(persons: number[]) {
  return map(persons, (person) => ({
    id: person,
    label: "person_" + person,
  }));
}

function getGeoAreas(geoAreas: GeoLocation[] | GeoTag[]) {
  return map(geoAreas, (geoArea) => ({
    id: geoArea.id,
    label: geoArea.label,
  }));
}

function buildCustomFieldsAsFacetsFromOptions(customFields: CustomField[]) {
  return reduce(
    customFields,
    (acc, field) => {
      acc[field.label] = {
        id: field.label,
        type: field.fixedValues ? "closed" : "opened",
        mode: field.singleValue ? "single" : "multi",
        values: map(rejectEmptyValues(field.values), (value) => ({
          color: value.color,
          id: value.value,
          label: value.value,
        })),
        custom: true,
        label: field.label,
        icon: "corners-tag",
        facetType: "valuesList",
        canBeExcluded: true,
      };
      return acc;
    },
    {} as FormatedFacets
  );
}

function buildFacetsFromOptions(
  queries: Query[],
  platforms: Platform[],
  logos: number[],
  persons: number[],
  corpora: Corpora[],
  groups: Group[],
  geoLocations: GeoLocation[],
  geoTags: GeoTag[]
) {
  return build({
    [Facet.Platforms]: platforms,
    [Facet.InfluencersTags]: groups,
    [Facet.Focuses]: queries,
    [Facet.Corpus]: corpora,
    [Facet.ImageRecognitionLogos]: getImageRecognitionLogos(logos),
    [Facet.ImageRecognitionPersonIdentifications]:
      getImageRecognitionPersonIdentifications(persons),
    [Facet.GeoLocations]: getGeoAreas(geoLocations),
    [Facet.GeoTags]: getGeoAreas(geoTags),
  }) as FormatedFacets;
}

export function useFacetsFromQueries(queries: Query[]) {
  const locale = useCurrentUserLocale();
  const platforms = useCurrentProjectPlatforms();
  const logos = useCurrentProject()?.brandLogos;
  const persons = useCurrentProject()?.personIdentifications;
  const geoLocations = useCurrentProject()?.geoArea?.geoLocations;
  const geoTags = useCurrentProject()?.geoArea?.geoTags;
  const customFields = useCustomFields();
  const isGlobalInsightPage = useIsCurrentInsightPageGlobal();
  const isInsightPage = useIsInsightPage();
  const corpora = useCorpora();
  const groups = useGroups();
  return useMemo(() => {
    return formatFacets(
      {
        ...buildFacetsFromOptions(
          queries,
          platforms,
          logos,
          persons,
          corpora,
          groups,
          geoLocations,
          geoTags
        ),
        ...buildCustomFieldsAsFacetsFromOptions(customFields),
      },
      locale,
      isGlobalInsightPage,
      isInsightPage
    );
  }, [
    queries,
    platforms,
    logos,
    persons,
    corpora,
    groups,
    geoLocations,
    geoTags,
    customFields,
    locale,
    isGlobalInsightPage,
    isInsightPage,
  ]);
}
